import React from 'react';

import { Button, Group, useMantineTheme } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { UseMutationResult, UseQueryResult } from 'react-query';
import {
  ErrorMessage,
  Invoice,
  InvoiceRetrieve,
  InvoiceUpdate,
  InvoiceUpdateNotes,
  InvoiceUpdatePaymentDate,
  InvoiceUpdateResponse,
} from 'types';

import { InvoiceDrawerState } from '../types';

interface InvoiceDrawerFooterProps {
  state: InvoiceDrawerState;
  setState: React.Dispatch<React.SetStateAction<InvoiceDrawerState>>;
  invoiceQuery: UseQueryResult<{
    data: InvoiceRetrieve;
  }>;
  updateInvoiceMutation: UseMutationResult<
    AxiosResponse<InvoiceUpdateResponse>,
    AxiosError<ErrorMessage>,
    InvoiceUpdate
  >;
  updateInvoiceNotesMutation: UseMutationResult<
    AxiosResponse<Invoice>,
    AxiosError<ErrorMessage>,
    InvoiceUpdateNotes
  >;
  updateInvoicePaymentDateMutation: UseMutationResult<
    AxiosResponse<Invoice>,
    AxiosError<ErrorMessage>,
    InvoiceUpdatePaymentDate
  >;
  isPaymentDateDirty: boolean;
  handleClose: () => void;
}

const InvoiceDrawerFooter = ({
  state,
  setState,
  invoiceQuery,
  updateInvoiceMutation,
  updateInvoiceNotesMutation,
  updateInvoicePaymentDateMutation,
  isPaymentDateDirty,
  handleClose,
}: InvoiceDrawerFooterProps): JSX.Element => {
  const theme = useMantineTheme();

  if (state === 'View') {
    return (
      <Group position='right' px='md' py={theme.spacing.lg}>
        <Button variant='subtle' onClick={handleClose}>
          Close
        </Button>
      </Group>
    );
  }

  if (state === 'Edit Invoice') {
    return (
      <Group position='right' px='md' py={theme.spacing.lg}>
        <Button variant='subtle' onClick={() => setState('View')}>
          Cancel
        </Button>
        <Button
          form='invoice-form'
          type='submit'
          disabled={invoiceQuery.isLoading}
          loading={updateInvoiceMutation.isLoading}
        >
          Save
        </Button>
      </Group>
    );
  }

  if (state === 'Edit Payment Date') {
    return (
      <Group position='right' px='md' py={theme.spacing.lg}>
        <Button variant='subtle' onClick={() => setState('View')}>
          Cancel
        </Button>
        <Button
          form='invoice-form'
          type='submit'
          disabled={invoiceQuery.isLoading || !isPaymentDateDirty}
          loading={updateInvoicePaymentDateMutation.isLoading}
        >
          Save
        </Button>
      </Group>
    );
  }

  if (state === 'Edit Notes') {
    return (
      <Group position='right' px='md' py={theme.spacing.lg}>
        <Button variant='subtle' onClick={() => setState('View')}>
          Cancel
        </Button>
        <Button
          form='invoice-notes'
          type='submit'
          disabled={invoiceQuery.isLoading}
          loading={updateInvoiceNotesMutation.isLoading}
        >
          Save
        </Button>
      </Group>
    );
  }

  return <></>;
};

export default InvoiceDrawerFooter;
